import React from "react";
import client from "@culturehq/client";
import { Loader, Success, Warning } from "@culturehq/components";

import { useProfile } from "app/context";
import { useFetch } from "app/hooks";
import { t, Trans } from "app/utils";

import PageHeader from "components/display/PageHeader";

const getSubdomain = subdomain => {
  if (subdomain.includes("-staging")) return "api-staging";
  if (subdomain === "eu-platform") return "uk-api";
  return "api";
};

const getConfigureLink = profile => {
  const subdomain = window.location.host.split(".")[0];
  const params = {
    client_id: "13d18b2e-129b-435c-9f52-54d29e10aa58",
    state: `chq-${profile.organization.id}-${profile.id}`,
    redirect_uri: `https://${getSubdomain(subdomain)}.culturehq.com/microsoft/callback`
  };

  const query = Object.keys(params).map(key => `${key}=${params[key]}`).join("&");
  return `https://login.microsoftonline.com/common/adminconsent?${query}`;
};

const MicrosoftIntegrationStatus = ({ integration }) => {
  const { profile } = useProfile();

  if (!integration) {
    const configureLink = getConfigureLink(profile);

    return (
      <>
        <Warning>
          <Trans>No integration is currently configured.</Trans>
        </Warning>
        <p>
          <Trans>
            To configure a new integration, click the following link and follow
            the prompts from Microsoft to authenticate and grant CultureHQ the
            necessary permissions: <a href={configureLink}>configure</a>.
          </Trans>
        </p>
      </>
    );
  }

  return (
    <Success>
      <Trans>
        An integration is configured for the Office 365 tenant:
        {" "}
        <code>{integration.tenant}</code>.
      </Trans>
    </Success>
  );
};

const MicrosoftIntegration = () => {
  const { error, fetching, json } = useFetch(client.getMicrosoftIntegration);
  const { integration } = json;

  if (error && error.status !== 404) {
    throw error;
  }

  return (
    <>
      <PageHeader title={t("Microsoft Integration")}>
        <Trans>
          Here you can configure an integration with Microsoft&#39;s Office 365.
          The integration will synchronize events in CultureHQ with the
          calendars of the users configured from within your Office 365
          organization.
        </Trans>
      </PageHeader>
      <Loader loading={fetching}>
        <MicrosoftIntegrationStatus integration={integration} />
      </Loader>
    </>
  );
};

export default MicrosoftIntegration;
